<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRevealOnScroll } from '@/composables/useRevealOnScroll'

const props = withDefaults(defineProps<{ variant?: 'public' | 'dashboard' }>(), { variant: 'public' })

const { t } = useI18n()
const { target, revealed } = useRevealOnScroll()

const values = ['v1', 'v2', 'v3', 'v4'] as const
const STAGGER = ['', 'motion-safe:delay-75', 'motion-safe:delay-150', 'motion-safe:delay-300'] as const
const isDashboard = computed(() => props.variant === 'dashboard')
</script>

<template>
  <section
    :class="isDashboard ? '' : 'bg-slate-50 py-16'"
    aria-labelledby="about-values-title"
  >
    <div ref="target" :class="isDashboard ? '' : 'max-w-7xl mx-auto px-4 sm:px-6 lg:px-8'">
      <div class="text-center mb-10">
        <p class="font-semibold text-sm uppercase tracking-widest" :class="isDashboard ? 'text-emerald-400' : 'text-emerald-600'">
          {{ t('about.values.eyebrow') }}
        </p>
        <h2
          id="about-values-title"
          class="text-3xl md:text-4xl font-bold mt-3"
          :class="isDashboard ? 'text-white' : 'text-slate-900'"
        >
          {{ t('about.values.title') }}
        </h2>
      </div>

      <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <div
          v-for="(key, index) in values"
          :key="key"
          class="rounded-xl p-6 border motion-safe:transition-all motion-safe:duration-500"
          :class="[
            isDashboard ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-100',
            revealed ? '' : 'motion-safe:opacity-0 motion-safe:translate-y-4',
            STAGGER[index],
          ]"
        >
          <svg class="w-8 h-8 text-emerald-500 mb-3" viewBox="0 0 40 40" fill="none" aria-hidden="true">
            <!-- v1: price tag -->
            <template v-if="key === 'v1'">
              <path d="M6 6h14l14 14-14 14L6 20V6z" stroke="currentColor" stroke-width="2.5" stroke-linejoin="round" />
              <circle cx="13" cy="13" r="2.5" fill="currentColor" />
            </template>
            <!-- v2: shield -->
            <template v-else-if="key === 'v2'">
              <path d="M20 4L8 10v10c0 7.18 5.14 13.9 12 15.5C27.86 33.9 32 27.18 32 20V10L20 4z" stroke="currentColor" stroke-width="2.5" stroke-linejoin="round" />
              <path d="M14 20l4 4 8-8" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
            </template>
            <!-- v3: map pin -->
            <template v-else-if="key === 'v3'">
              <path d="M20 4C13.373 4 8 9.373 8 16c0 9 12 20 12 20s12-11 12-20c0-6.627-5.373-12-12-12z" stroke="currentColor" stroke-width="2.5" />
              <circle cx="20" cy="16" r="4" stroke="currentColor" stroke-width="2" />
            </template>
            <!-- v4: two people -->
            <template v-else>
              <circle cx="15" cy="14" r="5" stroke="currentColor" stroke-width="2.5" />
              <circle cx="27" cy="16" r="4" stroke="currentColor" stroke-width="2.5" />
              <path d="M5 33c0-5.5 4.5-9 10-9s10 3.5 10 9M25 25c4.5 0 9 2.5 9 8" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" />
            </template>
          </svg>
          <h3 class="text-sm font-bold" :class="isDashboard ? 'text-white' : 'text-slate-900'">
            {{ t(`about.values.${key}.title`) }}
          </h3>
          <p class="text-sm mt-2 leading-relaxed" :class="isDashboard ? 'text-slate-400' : 'text-slate-500'">
            {{ t(`about.values.${key}.text`) }}
          </p>
        </div>
      </div>
    </div>
  </section>
</template>
